import { Fragment, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { QueueAction } from '@rossum/api-client/schemas';
import { FeatureIcon } from '@rossum/ui';
import { TablerIcon } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Box,
  ListItem,
  ListItemText,
  ListSubheader,
  TextField,
} from '@rossum/ui/material';
import { knownActionGroups } from '../actions';
import { getDefaultEvent } from '../actions/events';

type ActionAutocompleteProps = {
  value: QueueAction | undefined;
  onChange: (s: QueueAction | undefined) => void;
  selectableActions: QueueAction['type'][];
  options: {
    name: string;
    description: ReactNode;
    descriptionShort: ReactNode;
    serialized: QueueAction;
    groupIntlKey: string;
    icon: TablerIcon;
    resolveAction: (a: QueueAction) => boolean;
    supportedEvents: readonly [QueueAction['event'], ...QueueAction['event'][]];
  }[];
  error: string | undefined;
};

export const ActionAutocomplete = ({
  value,
  onChange,
  selectableActions,
  options,
  error,
}: ActionAutocompleteProps) => {
  const intl = useIntl();

  const stableValue = useMemo(
    () =>
      value
        ? (options.find(action => action.resolveAction(value)) ?? null)
        : null,
    [options, value]
  );

  return (
    <Autocomplete
      value={stableValue}
      onChange={(_, newValue) =>
        onChange(
          newValue
            ? {
                ...newValue.serialized,
                payload:
                  // It's better to always send payload to B/E (even empty) to get consistent error handling.
                  'payload' in newValue.serialized
                    ? newValue.serialized.payload
                    : {},
                event: getDefaultEvent(newValue),
              }
            : undefined
        )
      }
      disablePortal
      getOptionLabel={opt => opt.name}
      getOptionDisabled={opt =>
        !selectableActions.includes(opt.serialized.type)
      }
      options={options}
      renderOption={(
        { key, ...props },
        { name, descriptionShort, icon: Icon }
      ) => {
        return (
          <ListItem
            key={key}
            {...props}
            sx={{
              // Target the autocomplete option class directly for higher specificity
              '&.MuiAutocomplete-option': {
                padding: '0px 16px',
              },
            }}
          >
            <Box sx={{ mr: 2 }}>
              <FeatureIcon content={Icon} />
            </Box>
            <ListItemText primary={name} secondary={descriptionShort} />
          </ListItem>
        );
      }}
      groupBy={action => action.groupIntlKey}
      renderGroup={({ group, children, key }) => {
        const knownGroup = knownActionGroups.find(g => g === group) ?? 'other';
        return (
          <Fragment key={key}>
            <ListSubheader
              disableSticky
              sx={{
                borderTop: theme => `1px solid ${theme.palette.divider}`,
                py: 1,
                '&:first-of-type': { borderTop: 0 },
                lineHeight: '20px',
              }}
            >
              {intl.formatMessage({
                id: `features.queueSettings.rules.actionGroup.${knownGroup}`,
              })}
            </ListSubheader>
            {children}
          </Fragment>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          label={intl.formatMessage({
            id: `features.queueSettings.rules.action`,
          })}
          error={error !== undefined}
          helperText={error?.toString()}
          data-cy="action-autocomplete"
        />
      )}
    />
  );
};
